(() => {
  const instances = new Map();
  const desktop = window.matchMedia('(min-width: 1000px)');
  const hover = window.matchMedia('(hover: hover) and (pointer: fine)');

  const initialize = (header) => {
    if (instances.has(header)) return;
    const navigation = header.querySelector('[data-desktop-navigation]');
    const backdrop = header.querySelector('[data-navigation-backdrop]');
    if (!navigation || !backdrop) return;

    const controller = new AbortController();
    const listen = (target, type, callback, options = {}) => target.addEventListener(type, callback, { ...options, signal: controller.signal });
    const items = Array.from(navigation.querySelectorAll('[data-navigation-item]'));
    const topLinks = Array.from(navigation.querySelectorAll('.wp-desktop-navigation__trigger, .wp-desktop-navigation__link'));
    let activeItem = null;
    let openTimer;
    let closeTimer;

    const cancelTimers = () => {
      window.clearTimeout(openTimer);
      window.clearTimeout(closeTimer);
    };

    const positionBackdrop = () => {
      header.style.setProperty('--wp-navigation-bottom', `${Math.max(0, header.getBoundingClientRect().bottom)}px`);
    };

    const close = (restoreFocus = false) => {
      cancelTimers();
      if (!activeItem) return;
      const trigger = activeItem.querySelector('[data-navigation-trigger]');
      const panel = activeItem.querySelector('[data-navigation-panel]');
      trigger.setAttribute('aria-expanded', 'false');
      panel.hidden = true;
      panel.inert = true;
      activeItem = null;
      backdrop.hidden = true;
      header.removeAttribute('data-navigation-open');
      if (restoreFocus) trigger.focus({ preventScroll: true });
    };

    const open = (item) => {
      cancelTimers();
      if (!desktop.matches || activeItem === item) return;
      close();
      activeItem = item;
      const panel = item.querySelector('[data-navigation-panel]');
      item.querySelector('[data-navigation-trigger]').setAttribute('aria-expanded', 'true');
      panel.hidden = false;
      panel.inert = false;
      backdrop.hidden = false;
      header.setAttribute('data-navigation-open', '');
      header.querySelector('.wp-mobile-menu')?.removeAttribute('open');
      positionBackdrop();
    };

    // A hover preview must not hide a product link holding keyboard focus.
    const shelfHasFocus = () => activeItem?.querySelector('[data-navigation-shelf]:not([hidden])')?.contains(document.activeElement);

    const activateCategory = (category) => {
      const panel = category.closest('[data-navigation-panel]');
      panel.querySelectorAll('[data-navigation-category]').forEach((candidate) => {
        const selected = candidate === category;
        candidate.setAttribute('aria-selected', String(selected));
        candidate.tabIndex = selected ? 0 : -1;
      });
      panel.querySelectorAll('[data-navigation-shelf]').forEach((shelf) => {
        const selected = shelf.id === category.getAttribute('aria-controls');
        shelf.hidden = !selected;
        shelf.inert = !selected;
      });
    };

    items.forEach((item) => {
      const trigger = item.querySelector('[data-navigation-trigger]');
      listen(trigger, 'pointerenter', (event) => {
        if (!hover.matches || event.pointerType === 'touch' || shelfHasFocus()) return;
        cancelTimers();
        openTimer = window.setTimeout(() => {
          if (!shelfHasFocus()) open(item);
        }, activeItem ? 90 : 140);
      });
      listen(trigger, 'click', () => {
        if (activeItem === item) close();
        else open(item);
      });
    });

    navigation.querySelectorAll('.wp-desktop-navigation__link').forEach((link) => {
      listen(link, 'pointerenter', (event) => {
        if (hover.matches && event.pointerType !== 'touch' && !shelfHasFocus()) close();
      });
    });

    navigation.querySelectorAll('[data-navigation-category]').forEach((category) => {
      listen(category, 'pointerenter', (event) => {
        if (hover.matches && event.pointerType !== 'touch' && !shelfHasFocus()) activateCategory(category);
      });
      listen(category, 'click', () => activateCategory(category));
      listen(category, 'focus', () => activateCategory(category));
      listen(category, 'keydown', (event) => {
        const categories = Array.from(category.closest('[role="tablist"]').querySelectorAll('[data-navigation-category]'));
        const current = categories.indexOf(category);
        let next;
        if (event.key === 'ArrowDown') next = (current + 1) % categories.length;
        if (event.key === 'ArrowUp') next = (current - 1 + categories.length) % categories.length;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = categories.length - 1;
        if (next !== undefined) {
          event.preventDefault();
          categories[next].focus();
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          category.closest('[data-navigation-panel]').querySelector('[data-navigation-shelf]:not([hidden]) a')?.focus();
        }
      });
    });

    listen(navigation, 'pointerenter', cancelTimers);
    listen(navigation, 'pointerleave', (event) => {
      window.clearTimeout(openTimer);
      if (!hover.matches || event.pointerType === 'touch') return;
      // A pointer leaving must not hide the element a keyboard user is on.
      closeTimer = window.setTimeout(() => {
        if (!navigation.contains(document.activeElement)) close();
      }, 180);
    });
    listen(navigation, 'keydown', (event) => {
      const current = topLinks.indexOf(event.target);
      if (current < 0) return;
      if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
        event.preventDefault();
        const direction = event.key === 'ArrowRight' ? 1 : -1;
        close();
        topLinks[(current + direction + topLinks.length) % topLinks.length].focus();
      } else if (event.key === 'ArrowDown' && event.target.matches('[data-navigation-trigger]')) {
        event.preventDefault();
        const item = event.target.closest('[data-navigation-item]');
        open(item);
        item.querySelector('[data-navigation-category][aria-selected="true"]')?.focus();
      }
    });
    listen(navigation, 'focusout', (event) => {
      // Focusout runs before the destination receives focus. The temporary BODY
      // activeElement must not hide a clicked link before its click can fire.
      const nextFocus = event.relatedTarget;
      if (!nextFocus) return;
      if (!navigation.contains(nextFocus)) close();
      else if (activeItem && !activeItem.contains(nextFocus)) close();
    });
    listen(document, 'keydown', (event) => {
      if (event.key === 'Escape' && activeItem) {
        event.preventDefault();
        close(true);
      }
    });
    listen(document, 'pointerdown', (event) => {
      if (!navigation.contains(event.target)) close();
    });
    // Capture also covers keyboard-activated search / saved-book buttons before
    // their dialog code moves focus. Other header features can dispatch this event.
    listen(document, 'click', (event) => {
      if (event.target.closest('[data-book-search-link], [data-saved-books-open]')) close();
    }, { capture: true });
    listen(document, 'wonderpods:navigation:close', () => close());
    listen(window, 'resize', () => {
      const focusedInside = activeItem?.querySelector('[data-navigation-panel]').contains(document.activeElement);
      close(Boolean(focusedInside && desktop.matches));
      if (!desktop.matches && navigation.contains(document.activeElement)) header.querySelector('.wp-mobile-menu > summary')?.focus();
    });
    listen(window, 'scroll', () => { if (activeItem) positionBackdrop(); }, { passive: true });
    header.setAttribute('data-navigation-ready', '');
    instances.set(header, () => {
      close();
      controller.abort();
      header.removeAttribute('data-navigation-ready');
      instances.delete(header);
    });
  };

  const initializeAll = () => document.querySelectorAll('.wp-header').forEach(initialize);
  initializeAll();
  document.addEventListener('shopify:section:load', initializeAll);
  document.addEventListener('shopify:section:unload', (event) => {
    instances.forEach((destroy, header) => {
      if (event.target.contains(header)) destroy();
    });
  });
})();
